import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import TextLink from 'LuxKit/TextLink'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { invalidateTrip } from 'tripPlanner/hooks/api/common'
import type { RegenerateDayAiParams } from 'tripPlanner/api'
import { regenerateDayAi } from 'tripPlanner/api'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import RegenerateDayModal from 'tripPlanner/components/TripModal/RegenerateDayModal'
import type { TripItem } from 'tripPlanner/types/tripItem'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'

interface RegenerateButtonProps {
  tripId: string
  date: string
  startItems: Array<TripItem>
  onRegenerateStart: (date: string) => void
  onRegenerateEnd: (date: string) => void
  onRegenerateError: (date: string) => void
}

function RegenerateButton({ tripId, date, startItems, onRegenerateStart, onRegenerateEnd, onRegenerateError }: RegenerateButtonProps) {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const showModal = useContext(ModalContext)

  const failedToRegenerateMessage = intl.formatMessage({
    id: 'ZuDTu__W1aeCybwdFfWG',
    defaultMessage: 'Failed to regenerate day. Please try again.',
    description: 'Snackbar warning message when AI day regeneration fails',
  })

  const regenerateDayMutation = useMutation({
    mutationFn: ({ tripId, date, userInput, alternativeGroup }: RegenerateDayAiParams) =>
      regenerateDayAi({
        tripId,
        date,
        userInput,
        alternativeGroup,
      }),
    onSuccess: () => {
      invalidateTrip(queryClient, tripId)
      onRegenerateEnd(date)
    },
    onError: () => {
      onRegenerateError(date)
      showSnackbar(failedToRegenerateMessage, 'warning', {
        icon: <LineExclamationTriangleIcon />,
      })
    },
  })

  const handleRegenerateDay = useCallback((userInput?: string) => {
    onRegenerateStart(date)

    const trimmedUserInput = userInput?.trim()

    const payload: RegenerateDayAiParams = {
      tripId,
      date,
    }

    if (trimmedUserInput) {
      payload.userInput = trimmedUserInput
    }

    regenerateDayMutation.mutate(payload)
  }, [regenerateDayMutation, tripId, date, onRegenerateStart])

  const handleAlternativeGroupSubmit = useCallback((alternativeGroup: string) => {
    onRegenerateStart(date)

    const payload: RegenerateDayAiParams = {
      tripId,
      date,
      alternativeGroup,
    }

    regenerateDayMutation.mutate(payload)
  }, [regenerateDayMutation, tripId, date, onRegenerateStart])
  const handleOpenModal = useCallback(() => {
    invalidateTrip(queryClient, tripId)
    showModal(
      <RegenerateDayModal
        tripId={tripId}
        startItems={startItems}
        onRegenerateDaySubmit={handleRegenerateDay}
        onAlternativeGroupSubmit={handleAlternativeGroupSubmit}
      />,
    )
  }, [queryClient, tripId, showModal, startItems, handleRegenerateDay, handleAlternativeGroupSubmit])

  const replanLabel = intl.formatMessage({
    id: 'KAxPeF69Whbhk6A3l09j',
    defaultMessage: 'Replan this day',
    description: 'Link text to regenerate the current day itinerary using AI',
  })
  const planLabel = intl.formatMessage({
    id: 'L-kLU8cBRBl7RBK99z35',
    defaultMessage: 'Plan day with AI',
    description: 'Link text to generate an AI-powered day itinerary for the first time',
  })

  const text = startItems.length > 0 ? replanLabel : planLabel
  return (
    <Group direction="vertical" gap={4}>
      <TextLink
        size="medium"
        startIcon={<SolidSparklesIcon />}
        onClick={handleOpenModal}
      >
        {text}
      </TextLink>
    </Group>
  )
}

export default RegenerateButton
